import React, { useEffect, useState } from 'react';
import { api, formatApiError, resolveImageUrl } from '../../lib/api';
import ImageUploader from '../../components/admin/ImageUploader';
import TranslateButton from '../../components/admin/TranslateButton';

const emptyForm = { category: '', title: '', link: '', image: '', category_en: '', title_en: '' };

const NewsList = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // { id?, ...fields } or null
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/news');
      setItems(data);
      setError('');
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        category: editing.category,
        title: editing.title,
        link: editing.link,
        image: editing.image,
        category_en: editing.category_en || '',
        title_en: editing.title_en || '',
      };
      if (editing.id) {
        await api.put(`/admin/news/${editing.id}`, payload);
      } else {
        await api.post('/admin/news', payload);
      }
      setEditing(null);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await api.delete(`/admin/news/${id}`);
      setConfirming(null);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-news-list">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-light">Actualités</h1>
          <p className="text-gray-500 font-light mt-1">
            {loading ? 'Chargement...' : `${items.length} actualité(s)`}
          </p>
        </div>
        {!editing && (
          <button
            onClick={() => setEditing({ ...emptyForm })}
            className="text-sm font-light tracking-wider uppercase bg-black text-white px-5 py-3 hover:bg-[#d94e47] transition-colors"
            data-testid="admin-new-news"
          >
            + Nouvelle actualité
          </button>
        )}
      </div>

      {error && (
        <p className="text-[#d94e47] font-light mb-4" data-testid="news-error">{error}</p>
      )}

      {editing && (
        <form
          onSubmit={onSave}
          className="bg-white border border-gray-200 p-8 mb-10 max-w-3xl"
          data-testid="admin-news-form"
        >
          <h2 className="text-xl font-light mb-6">
            {editing.id ? `Modifier actualité #${editing.id}` : 'Nouvelle actualité'}
          </h2>
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldRow label="Catégorie" value={editing.category}
                onChange={(v) => setEditing((f) => ({ ...f, category: v }))} testId="news-field-category" />
              <FieldRowWithTranslate
                label="Catégorie (EN)" value={editing.category_en}
                onChange={(v) => setEditing((f) => ({ ...f, category_en: v }))}
                sourceValue={editing.category}
                testId="news-field-category-en"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldRow label="Titre *" value={editing.title} required
                onChange={(v) => setEditing((f) => ({ ...f, title: v }))} testId="news-field-title" />
              <FieldRowWithTranslate
                label="Titre (EN)" value={editing.title_en}
                onChange={(v) => setEditing((f) => ({ ...f, title_en: v }))}
                sourceValue={editing.title}
                testId="news-field-title-en"
              />
            </div>
            <FieldRow label="Lien (ex: /fr/projets/slug)" value={editing.link}
              onChange={(v) => setEditing((f) => ({ ...f, link: v }))} testId="news-field-link" />
            <ImageUploader
              value={editing.image}
              onUploaded={(url) => setEditing((f) => ({ ...f, image: url }))}
              label="Image"
              testId="news-field-image"
            />
          </div>

          <div className="mt-8 flex gap-4">
            <button
              type="submit"
              disabled={saving}
              className="bg-black text-white px-6 py-3 text-sm font-light tracking-wider uppercase hover:bg-[#d94e47] transition-colors disabled:opacity-50"
              data-testid="news-form-submit"
            >
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="px-6 py-3 text-sm font-light tracking-wider uppercase text-gray-500 hover:text-black"
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-gray-200">
        <div className="grid grid-cols-[80px_1fr_1fr_auto] gap-4 px-6 py-3 text-xs font-light tracking-wider uppercase text-gray-500 border-b border-gray-200">
          <span></span>
          <span>Titre</span>
          <span>Catégorie</span>
          <span className="text-right">Actions</span>
        </div>
        {items.map((n) => (
          <div
            key={n.id}
            className="grid grid-cols-[80px_1fr_1fr_auto] gap-4 px-6 py-4 border-b border-gray-100 items-center"
            data-testid={`news-row-${n.id}`}
          >
            <div className="w-[64px] h-[48px] bg-gray-100 overflow-hidden">
              {n.image && (
                <img src={resolveImageUrl(n.image)} alt="" className="w-full h-full object-cover" />
              )}
            </div>
            <p className="text-sm font-light">{n.title}</p>
            <p className="text-sm font-light text-gray-500">{n.category}</p>
            <div className="flex gap-4 justify-end text-xs font-light tracking-wider uppercase">
              <button
                onClick={() => setEditing({ ...n })}
                className="text-black hover:text-[#d94e47]"
                data-testid={`edit-news-${n.id}`}
              >
                Modifier
              </button>
              {confirming === n.id ? (
                <>
                  <button
                    onClick={() => onDelete(n.id)}
                    className="text-[#d94e47] hover:underline"
                    data-testid={`confirm-delete-news-${n.id}`}
                  >
                    Confirmer
                  </button>
                  <button
                    onClick={() => setConfirming(null)}
                    className="text-gray-400 hover:underline"
                  >
                    Annuler
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirming(n.id)}
                  className="text-gray-500 hover:text-[#d94e47]"
                  data-testid={`delete-news-${n.id}`}
                >
                  Supprimer
                </button>
              )}
            </div>
          </div>
        ))}
        {!loading && items.length === 0 && (
          <p className="text-center text-gray-500 font-light py-12">Aucune actualité.</p>
        )}
      </div>
    </div>
  );
};

const FieldRow = ({ label, value, onChange, required, testId }) => (
  <label className="block">
    <span className="block text-xs font-light tracking-wider uppercase text-gray-500 mb-2">{label}</span>
    <input
      type="text"
      required={required}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47] transition-colors"
      data-testid={testId}
    />
  </label>
);

const FieldRowWithTranslate = ({ label, value, onChange, sourceValue, testId }) => (
  <div className="block">
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-light tracking-wider uppercase text-gray-500">{label}</span>
      <TranslateButton value={sourceValue} onTranslated={onChange} testId={`${testId}-translate`} />
    </div>
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 px-3 py-2 font-light focus:outline-none focus:border-[#d94e47] transition-colors"
      data-testid={testId}
    />
  </div>
);

export default NewsList;
